/**
 * Live — explore page: browse active live rooms + Go Live dialog.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Radio, Users, Eye, Video, RefreshCw, ImagePlus, X, Camera } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { useAuthStore } from '../store/authStore';
import apiClient from '../utils/api';
import { toast } from 'sonner';

export const BG_PRESETS = {
  midnight: 'linear-gradient(135deg,#0f0c29,#302b63,#24243e)',
  sunset: 'linear-gradient(135deg,#41295a,#f64f59)',
  ocean: 'linear-gradient(135deg,#0f2027,#2c5364)',
  neon: 'linear-gradient(135deg,#12c2e9,#c471ed,#f64f59)',
  forest: 'linear-gradient(135deg,#134e5e,#71b280)',
  candy: 'linear-gradient(135deg,#ff9a9e,#a18cd1)',
  fire: 'linear-gradient(135deg,#1f1c18,#8e0e00)',
  gold: 'linear-gradient(135deg,#232526,#b8860b)',
};

export default function Live() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState('');
  const [background, setBackground] = useState('midnight');
  const [maxBoxes, setMaxBoxes] = useState(4);
  const [cover, setCover] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = React.useRef(null);
  const camStreamRef = React.useRef(null);

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 1280, height: 720 } });
      camStreamRef.current = stream;
      setShowCamera(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = stream; }, 50);
    } catch (err) {
      toast.error('Camera access denied');
    }
  };

  const closeCamera = () => {
    camStreamRef.current?.getTracks().forEach((tr) => tr.stop());
    camStreamRef.current = null;
    setShowCamera(false);
  };

  const captureThumbnail = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = 640;
    canvas.height = Math.round(640 * (video.videoHeight / video.videoWidth));
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    closeCamera();
    setUploadingCover(true);
    canvas.toBlob(async (blob) => {
      try {
        const fd = new FormData();
        fd.append('file', new File([blob], 'live_cover.jpg', { type: 'image/jpeg' }));
        const res = await apiClient.post('/upload-image-compressed?quality=55', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setCover(res.data.url);
        toast.success('Thumbnail captured!');
      } catch (err) {
        toast.error('Thumbnail upload failed');
      }
      setUploadingCover(false);
    }, 'image/jpeg', 0.85);
  };

  const handleCoverPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiClient.post('/upload-image-compressed?quality=55', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCover(res.data.url);
      toast.success('Cover added!');
    } catch (err) {
      toast.error('Cover upload failed');
    }
    setUploadingCover(false);
  };

  const fetchRooms = useCallback(async () => {
    try {
      const res = await apiClient.get('/live/rooms');
      setRooms(res.data.rooms || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchRooms();
    const t = setInterval(fetchRooms, 5000);
    return () => clearInterval(t);
  }, [fetchRooms]);

  const goLive = () => {
    if (!isAuthenticated) {
      toast.error('Sign in to go live');
      return;
    }
    navigate('/live/new', {
      state: { create: { title: title || `${user?.name || 'My'} Live`, background, max_boxes: maxBoxes, cover } },
    });
  };

  return (
    <div className="relative z-10 min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
              <Radio className="w-8 h-8 text-red-500" /> Live
            </h1>
            <p className="text-gray-400">Watch live streams or start your own — camera, mic, guests and chat</p>
          </div>
          <Button
            onClick={() => setShowCreate(true)}
            className="bg-gradient-to-r from-red-600 to-pink-600 hover:opacity-90"
            data-testid="go-live-btn"
          >
            <Video className="w-4 h-4 mr-2" /> Go Live
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><RefreshCw className="w-8 h-8 text-purple-400 animate-spin" /></div>
        ) : rooms.length === 0 ? (
          <div className="text-center py-20" data-testid="live-empty-state">
            <Radio className="w-14 h-14 text-white/20 mx-auto mb-4" />
            <p className="text-white/60 text-lg">Nobody is live right now</p>
            <p className="text-white/35 text-sm mt-1">Be the first — tap Go Live and start the party</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="live-rooms-grid">
            {rooms.map((room) => (
              <button
                key={room.room_id}
                onClick={() => navigate(`/live/${room.room_id}`)}
                className="text-left rounded-xl overflow-hidden border border-white/10 hover:border-red-500/60 transition-all hover:scale-[1.02] group"
                data-testid={`live-room-${room.room_id}`}
              >
                <div
                  className="h-32 relative flex items-end p-3"
                  style={room.cover
                    ? { backgroundImage: `url(${room.cover})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                    : { background: BG_PRESETS[room.background] || BG_PRESETS.midnight }}
                >
                  <span className={`absolute top-2 left-2 flex items-center gap-1.5 ${room.host_away ? 'bg-amber-500 text-black' : 'bg-red-600 text-white animate-pulse'} text-[11px] font-bold px-2 py-0.5 rounded-full`} data-testid={`live-badge-${room.room_id}`}>
                    <Radio className="w-3 h-3" /> {room.host_away ? 'HOST AWAY' : 'LIVE'}
                    <span className="flex items-center gap-0.5 border-l border-white/40 pl-1.5 font-semibold">
                      <Eye className="w-3 h-3" /> {room.viewer_count}
                    </span>
                  </span>
                  <h3 className="text-white font-semibold text-lg drop-shadow line-clamp-1">{room.title}</h3>
                </div>
                <div className="bg-black/40 p-3 flex items-center justify-between">
                  <span className="text-white/80 text-sm">{room.host_name}</span>
                  <span className="text-white/45 text-xs flex items-center gap-1">
                    <Users className="w-3 h-3" /> {room.boxes_used}/{room.max_boxes} on cam
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Go Live dialog */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={(e) => e.target === e.currentTarget && setShowCreate(false)}>
          <Card className="w-full max-w-md bg-gray-900 border-red-500/30" data-testid="go-live-dialog">
            <CardContent className="p-5 space-y-4">
              <h2 className="text-white text-lg font-bold flex items-center gap-2">
                <Radio className="w-5 h-5 text-red-500" /> Start your Live
              </h2>
              <div>
                <label className="text-white/70 text-sm block mb-1">Title</label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What's happening?" maxLength={80} data-testid="live-title-input" />
              </div>
              <div>
                <label className="text-white/70 text-sm block mb-1">Cover photo <span className="text-white/35">(camera roll or take a photo)</span></label>
                {cover ? (
                  <div className="relative h-24 rounded-lg overflow-hidden border border-white/20">
                    <img src={cover} alt="cover" className="w-full h-full object-cover" data-testid="live-cover-preview" />
                    <button onClick={() => setCover(null)} className="absolute top-1 right-1 bg-black/70 rounded-full p-1" data-testid="live-cover-remove">
                      <X className="w-3.5 h-3.5 text-white" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <label className="flex-1 flex items-center justify-center gap-2 h-16 rounded-lg border-2 border-dashed border-white/25 hover:border-white/50 cursor-pointer text-white/60 text-sm" data-testid="live-cover-picker">
                      <ImagePlus className="w-4 h-4" />
                      {uploadingCover ? 'Uploading…' : 'Upload photo'}
                      <input type="file" accept="image/*" className="hidden" onChange={handleCoverPick} data-testid="live-cover-input" />
                    </label>
                    <button
                      onClick={openCamera}
                      className="flex-1 flex items-center justify-center gap-2 h-16 rounded-lg border-2 border-dashed border-white/25 hover:border-white/50 text-white/60 text-sm"
                      data-testid="live-cover-camera-btn"
                    >
                      <Camera className="w-4 h-4" /> Take a photo
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label className="text-white/70 text-sm block mb-1">Background</label>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(BG_PRESETS).map(([id, css]) => (
                    <button key={id} onClick={() => setBackground(id)} title={id}
                      className={`w-10 h-10 rounded-lg border-2 ${background === id ? 'border-white scale-110' : 'border-white/20'}`}
                      style={{ background: css }} data-testid={`live-bg-${id}`} />
                  ))}
                </div>
              </div>
              <div>
                <label className="text-white/70 text-sm block mb-1">Camera boxes</label>
                <div className="flex gap-2 flex-wrap">
                  {[1, 2, 4, 6, 9, 12].map((n) => (
                    <button key={n} onClick={() => setMaxBoxes(n)}
                      className={`w-10 h-10 rounded-lg border-2 text-sm font-semibold ${maxBoxes === n ? 'border-red-500 bg-red-500/20 text-white' : 'border-white/20 text-white/60'}`}
                      data-testid={`live-boxes-${n}`}>{n}</button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowCreate(false)}>Cancel</Button>
                <Button className="flex-1 bg-gradient-to-r from-red-600 to-pink-600" onClick={goLive} data-testid="start-live-btn">
                  Go Live
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {/* Camera capture modal for cover thumbnail */}
      {showCamera && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4" data-testid="live-camera-modal">
          <div className="w-full max-w-lg space-y-3">
            <video ref={videoRef} autoPlay playsInline muted className="w-full rounded-xl bg-black" style={{ transform: 'scaleX(-1)' }} />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={closeCamera} data-testid="camera-cancel-btn">Cancel</Button>
              <Button className="flex-1 bg-gradient-to-r from-red-600 to-pink-600" onClick={captureThumbnail} data-testid="camera-capture-btn">
                <Camera className="w-4 h-4 mr-2" /> Capture
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
